import { useMemo, useState } from 'react'
import { Alert, Button, Card, Icon, Tag, Text, Title } from '@nimbus-ds/components'
import { CheckIcon, CopyIcon, CodeIcon } from '@nimbus-ds/icons'

interface BundleBootstrapCardProps {
  storeId: string
}

const ASSETS_CDN_URL =
  (import.meta.env.VITE_ASSETS_CDN_URL as string | undefined) ||
  'https://assets.dev.tn-xp.eclab.pro'

export function BundleBootstrapCard({ storeId }: BundleBootstrapCardProps) {
  const [copied, setCopied] = useState(false)

  const snippet = useMemo(
    () =>
      `<style id="tnx-af">html{opacity:0 !important}</style>
<script src="${ASSETS_CDN_URL}/bundles/${storeId}.js"></script>
<script>setTimeout(function(){var s=document.getElementById('tnx-af');if(s)s.remove();},2000);</script>`,
    [storeId]
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      // clipboard may be unavailable in non-https contexts; fall back to selection
      const ta = document.createElement('textarea')
      ta.value = snippet
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch {}
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon source={<CodeIcon />} />
          <Title as="h3">Snippet de instalación (anti-flicker)</Title>
          <Tag appearance="neutral">Recomendado</Tag>
        </div>
        <Text>
          Pegá este código una sola vez en el <code className="rounded bg-muted px-1 py-0.5 text-xs">&lt;head&gt;</code> de tu tienda.
          Carga las variantes activas antes de que el navegador pinte la página, eliminando el parpadeo en visitas con cache fría.
        </Text>
      </Card.Header>

      <Card.Body className="space-y-4">
        <div className="relative">
          <pre className="overflow-x-auto rounded-lg border bg-muted/40 p-4 pr-14 text-xs leading-relaxed">
            <code>{snippet}</code>
          </pre>
          <Button
            appearance={copied ? 'primary' : 'neutral'}
            onClick={handleCopy}
            className="absolute right-2 top-2"
          >
            {copied ? (
              <>
                <Icon source={<CheckIcon />} color="currentColor" />
                Copiado
              </>
            ) : (
              <>
                <Icon source={<CopyIcon />} color="currentColor" />
                Copiar
              </>
            )}
          </Button>
        </div>

        <div className="space-y-3">
          <Title as="h4">Cómo instalarlo</Title>
          <ol className="space-y-2">
            <li className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">1</span>
              <Text as="span">Abrí tu administrador de TiendaNube → <strong>Diseño</strong> → <strong>Editar HTML/CSS</strong>.</Text>
            </li>
            <li className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">2</span>
              <Text as="span">Buscá el archivo del template principal (generalmente <code className="rounded bg-muted px-1 py-0.5 text-xs">layout.tpl</code>) y pegá el snippet justo después de la etiqueta <code className="rounded bg-muted px-1 py-0.5 text-xs">&lt;head&gt;</code>.</Text>
            </li>
            <li className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">3</span>
              <Text as="span">Guardá los cambios. Los experimentos activos se aplicarán automáticamente en la próxima carga de página.</Text>
            </li>
          </ol>
        </div>

        <Alert appearance="primary">
          El bundle se regenera automáticamente cuando creás, modificás o eliminás un experimento.
          No necesitás volver a copiar el snippet — la URL del script siempre apunta a la versión más reciente.
        </Alert>

        <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
          <div className="font-medium text-foreground mb-1">URL del bundle</div>
          <code className="break-all">{ASSETS_CDN_URL}/bundles/{storeId}.js</code>
        </div>
      </Card.Body>
    </Card>
  )
}
